import { HttpUrlEncodingCodec } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AuthConfig, OAuthService, OAuthStorage } from "angular-oauth2-oidc";
import { JwksValidationHandler } from "angular-oauth2-oidc-jwks";
import { Guid } from "guid-typescript";
import { CookieService } from "ngx-cookie-service";
import { ReplaySubject } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment.prod";
import { AppConfig, IAppConfig } from "../models/core/app-config";
import { AppConfigService } from "./app-config.service";
import { LogService } from "./log.services";
import { WindowRef } from "./window-ref.service";
import { Router } from "@angular/router";

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  
  private appConfig: IAppConfig = new AppConfig(); 
  private authConfig = new AuthConfig();
  private inProgress: boolean = false;
  private authCompleteSource = new ReplaySubject<boolean>(1);
  authComplete = this.authCompleteSource.asObservable();

  constructor(private appConfigService: AppConfigService,
    private oauthService: OAuthService,
    private cookieService: CookieService,
    private window: WindowRef,
    private logService: LogService,
    private authStorage: OAuthStorage,
    private router: Router) { }

  public isUserLoggedIn() {
    return this.authComplete;
  }

  public userLoggedIn(redirectPath: string| null = null) {
    if (this.inProgress)
      return this.authComplete;

    this.inProgress = true;
    this.authenticate(redirectPath);
    return this.authComplete;
  }

  public getToken(): string {
    return this.authStorage.getItem('access_token') || '';
  }

  public getMembershipId() {
    const claims: any = this.oauthService.getIdentityClaims()
    if (claims)
      return claims.healthcare_membership ? claims.healthcare_membership : '';
    else return '';
  }
  public getMemberEmail() {
    const claims: any = this.oauthService.getIdentityClaims()
    if (claims)
      return claims.email ? claims.username : '';
    else return '';
  }

  public userLogout() {
    this.logService.debug('user logged out');

    var tokenId = this.authStorage.getItem('id_token') || "";
    sessionStorage.clear();

    this.oauthService.logOut({ 'id_token_hint': tokenId, 'post_logout_redirect_uri': this.appConfig.logoutUri });
  }

  public getStateOptions(): any {
    return this.oauthService.options;
  }

  public checkAuthState() {
    this.getConfig().subscribe(_ => {
      this.oauthService.configure(this.authConfig);
      this.oauthService.tokenValidationHandler = new JwksValidationHandler();

      this.oauthService.loadDiscoveryDocument().then((doc) => {

        this.oauthService.tryLogin({ preventClearHashAfterLogin: true })
          .then(() => {
            this.oauthService.setupAutomaticSilentRefresh(this.authConfig);

            if (this.oauthService.state && this.oauthService.hasValidAccessToken()) {
              var path = this.cookieService.get(this.oauthService.state + "redirect");
              if (path != null) {
                // return user to original journey route
                this.router.navigate([path], { replaceUrl: true });
                this.cookieService.delete(this.oauthService.state + "redirect");
              }
              this.cookieService.delete(this.oauthService.state);
            }
            this.authCompleteSource.next(this.oauthService.hasValidAccessToken());     
          });
      });
    });
  }

  private authenticate(redirectPath: string | null = null) {
    this.getConfig().subscribe(_ => {
      this.oauthService.configure(this.authConfig);
      this.oauthService.tokenValidationHandler = new JwksValidationHandler();

      this.oauthService.loadDiscoveryDocument().then((doc) => {
        this.oauthService.tryLogin({ preventClearHashAfterLogin: true})
          .catch(err => {
            console.error(err);
          })
          .then(() => {
            if (this.oauthService.state && this.cookieService.check(this.oauthService.state)) {
              var storedPath = this.cookieService.get(this.oauthService.state + "redirect");

              if (storedPath != null) {
                this.router.navigate([storedPath], { replaceUrl: true });
                this.cookieService.delete(this.oauthService.state + "redirect");
              }
              this.cookieService.delete(this.oauthService.state);
            }

            if (!this.oauthService.hasValidAccessToken()) {
              const name = Guid.create().toString();
              this.cookieService.set(name, this.window.nativeWindow.location.href, 1);

              // Preserve the query
              if (redirectPath != null) {
                this.cookieService.set(name + "redirect", redirectPath);
              } else if (this.window.nativeWindow.location.pathname != "/" && this.window.nativeWindow.location.pathname.length > 0) {
                var path = this.window.nativeWindow.location.pathname.slice(1, this.window.nativeWindow.location.pathname.length);
                this.cookieService.set(name + "redirect", path);
              }

              this.oauthService.initImplicitFlow(name);
              this.authCompleteSource.next(false);
            } else {
              this.logService.debug('user logged in');
              this.oauthService.setupAutomaticSilentRefresh(this.authConfig);
              this.authCompleteSource.next(true);
            }
          });
      }).catch(err => {
        this.logService.error("Error connecting to identity server");
      });
    });
  }

  private getConfig() {
    return this.appConfigService.getAppConfig()
      .pipe(map((data: AppConfig) => {
        this.appConfig = data;

        this.authConfig = new AuthConfig();
        this.authConfig.redirectUri = this.appConfig.redirectUri;
        this.authConfig.clientId = this.appConfig.clientId;
        this.authConfig.requireHttps = true;
        this.authConfig.scope = this.appConfig.scope;
        this.authConfig.issuer = this.appConfig.authorityUrl;
        //this.authConfig.requireHttps = false; //for local dev only
        this.authConfig.dummyClientSecret = this.appConfig.secret;
        this.authConfig.responseType = "code id_token token";
        this.authConfig.postLogoutRedirectUri = this.appConfig.logoutUri;

      }));
  }

  private urlsMatch(u1: string, u2: string): boolean {
    u1 = this.stripTrailing(u1, '#');
    u2 = this.stripTrailing(u2, '#');

    return u1 === u2;
  }

  private stripTrailing(value: string, trail: string): string {
    return value.endsWith(trail) ? value.slice(0, trail.length) : value;
  }
}
